import type { Coordinates } from '../../types';
import type { MemberLocation } from '../../types/presence';
import type { BasemapId } from '../../config/maplibre';
import { geolocationService } from '../../services/geolocation/geolocationService';
import { getGeolocationErrorMessage } from '../../utils/geolocation/errorMessages';
import { NotificationService } from '../../services/notificationService';
import {
  GEOLOCATION_HIGH_ACCURACY,
  GEOLOCATION_TIMEOUT_MS,
  GEOLOCATION_MAX_AGE_LONG_MS,
} from '../../constants/geolocation';
import i18n from '../../config/i18n';

// ===== SLICE INTERFACE =====

/**
 * Basemap resolution override for public share pages. When set,
 * `selectResolvedBasemapId` ignores workspace settings entirely and resolves
 * the basemap from the share's allowlist + the viewer's pick stored in
 * `userBasemapByWorkspace` under the synthetic `shareKey`.
 */
export interface PublicBasemapContext {
  /** Synthetic preference key (`share:<token>`). */
  shareKey: string;
  allowedBasemaps: BasemapId[];
  /** Owner-configured start basemap; one of `allowedBasemaps`. Undefined = first allowed. */
  defaultBasemap?: BasemapId;
}

export interface ZoomToOptions {
  /**
   * Pixels reserved by overlapping UI from the bottom of the map (e.g. mobile
   * preview panel). Used to keep the zoomed geometry centered in the visible
   * (uncovered) area instead of the full viewport.
   */
  bottomPadding?: number;
}

export interface MapSlice {
  map: {
    tempRoad: string | null;
    zoomToCoordinates: Coordinates | null;
    zoomToBottomPadding: number;
    userLocation: Coordinates | null;
    isLocatingUser: boolean;
    is3DMode: boolean;
    isTransitioning: boolean;
    /** Persisted opt-in: broadcast my live location to the workspace. */
    shareLocation: boolean;
    /** My own current position (transient); drives the always-on local dot. */
    liveLocation: MemberLocation | null;
    /** Current geolocation permission (transient); drives a consent-revoked infobar. */
    locationPermission: PermissionState | null;
    /** Per-workspace user basemap preference, keyed by workspace id (or share key). */
    userBasemapByWorkspace: Record<string, BasemapId>;
    /** Non-null only on public share pages — overrides workspace basemap resolution. */
    publicBasemapContext: PublicBasemapContext | null;
    setTempRoad: (road: string | null) => void;
    zoomTo: (coords: Coordinates | null, options?: ZoomToOptions) => void;
    setUserLocation: (coords: Coordinates | null) => void;
    setIsLocatingUser: (isLocating: boolean) => void;
    setShareLocation: (enabled: boolean) => void;
    setLiveLocation: (location: MemberLocation | null) => void;
    setLocationPermission: (state: PermissionState | null) => void;
    goToUserLocation: () => Promise<void>;
    toggle3DMode: () => void;
    set3DMode: (enabled: boolean) => void;
    setTransitioning: (transitioning: boolean) => void;
    setUserBasemapForWorkspace: (workspaceId: string, id: BasemapId) => void;
    setPublicBasemapContext: (context: PublicBasemapContext | null) => void;
  };
}

// ===== SLICE CREATOR =====
// Phase 2: Full implementation with Immer v10

export const createMapSlice = (
  set: (fn: (state: MapSlice) => void) => void,
  get: () => MapSlice,
  _store: unknown
) => ({
  map: {
    tempRoad: null,
    zoomToCoordinates: null,
    zoomToBottomPadding: 0,
    userLocation: null,
    isLocatingUser: false,
    is3DMode: false,
    isTransitioning: false,
    shareLocation: false,
    liveLocation: null,
    locationPermission: null,
    userBasemapByWorkspace: {},
    publicBasemapContext: null,

    setTempRoad: (road: string | null) => {
      set((state) => {
        state.map.tempRoad = road;
      });
    },

    zoomTo: (coords: Coordinates | null, options?: ZoomToOptions) => {
      set((state) => {
        state.map.zoomToCoordinates = coords;
        state.map.zoomToBottomPadding = coords
          ? (options?.bottomPadding ?? 0)
          : 0;
      });
    },

    setUserLocation: (coords: Coordinates | null) => {
      set((state) => {
        state.map.userLocation = coords;
      });
    },

    setIsLocatingUser: (isLocating: boolean) => {
      set((state) => {
        state.map.isLocatingUser = isLocating;
      });
    },

    setShareLocation: (enabled: boolean) => {
      set((state) => {
        state.map.shareLocation = enabled;
      });
    },

    setLiveLocation: (location: MemberLocation | null) => {
      set((state) => {
        state.map.liveLocation = location;
      });
    },

    setLocationPermission: (state: PermissionState | null) => {
      set((draft) => {
        draft.map.locationPermission = state;
      });
    },

    toggle3DMode: () => {
      set((state) => {
        state.map.is3DMode = !state.map.is3DMode;
      });
    },

    set3DMode: (enabled: boolean) => {
      set((state) => {
        state.map.is3DMode = enabled;
      });
    },

    setTransitioning: (transitioning: boolean) => {
      set((state) => {
        state.map.isTransitioning = transitioning;
      });
    },

    setUserBasemapForWorkspace: (workspaceId: string, id: BasemapId) => {
      set((state) => {
        state.map.userBasemapByWorkspace[workspaceId] = id;
      });
    },

    setPublicBasemapContext: (context: PublicBasemapContext | null) => {
      set((state) => {
        state.map.publicBasemapContext = context;
      });
    },

    goToUserLocation: async () => {
      const { setIsLocatingUser, setUserLocation, zoomTo } = get().map;

      setIsLocatingUser(true);

      try {
        const result = await geolocationService.getCurrentPosition({
          enableHighAccuracy: GEOLOCATION_HIGH_ACCURACY,
          timeout: GEOLOCATION_TIMEOUT_MS,
          maximumAge: GEOLOCATION_MAX_AGE_LONG_MS,
        });

        if (result.success && result.coordinates) {
          const coords: Coordinates = result.coordinates;

          setUserLocation(coords);
          zoomTo(coords); // Reuse existing zoom action
        } else {
          // Handle error from geolocationService
          const errorMessage = getGeolocationErrorMessage(
            result.errorCode || 'POSITION_UNAVAILABLE',
            i18n.t
          );

          NotificationService.showError(errorMessage);
        }
      } catch (_error) {
        // Fallback error handling (unexpected errors)
        const errorMessage = getGeolocationErrorMessage(
          'POSITION_UNAVAILABLE',
          i18n.t
        );

        NotificationService.showError(errorMessage);
      } finally {
        setIsLocatingUser(false);
      }
    },
  },
});
